import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbMail } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { formatIsoTimestamp } from "../util/dateFormat";
import { senderDisplayName } from "./emailRenderUtils";
import { type EmailListPayload, parseEmailListPayload } from "./emailSchema";

interface EmailListRendererProps {
  block: StreamingBlock;
}

// Sender, subject, and snippet are untrusted and rendered as text only.
export const EmailListRenderer = (props: EmailListRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<EmailListPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseEmailListPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("email_list_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().messages.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <TbMail class="size-4 shrink-0 text-brand" />
                <span class="text-sm font-medium text-primary-text">{rt.t("email_list_card.title")}</span>
                <span class="ml-auto text-xs text-muted-text">
                  {data().totalResults === 1
                    ? rt.t("email_list_card.results_one")
                    : rt.t("email_list_card.results", { count: String(data().totalResults) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().messages}>
                  {(message) => (
                    <li class="px-3 py-2">
                      <div class="flex items-baseline justify-between gap-3">
                        <span class="flex min-w-0 items-center gap-2">
                          <Show when={message.unread}>
                            <span
                              class="size-2 shrink-0 rounded-full bg-brand"
                              aria-label={rt.t("email_list_card.unread")}
                            />
                          </Show>
                          <span class="truncate text-sm text-primary-text">{senderDisplayName(message.from)}</span>
                        </span>
                        <span class="shrink-0 text-xs text-muted-text">{formatIsoTimestamp(message.date)}</span>
                      </div>
                      <h3 class="mt-0.5 truncate text-sm font-bold text-primary-text">{message.subject}</h3>
                      <p class="mt-0.5 line-clamp-2 text-xs text-muted-text">{message.snippet}</p>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
